import { Injectable } from '@angular/core';

import { ApiService } from '@core/services/common';
import { API_ENDPOINT, LOCAL_STORAGE_KEY } from '@core/configs';
import { LogConfig } from '@core/configs/saucer-log.config';
import { LogAction, LogType, ScreenName } from '@core/models/enums';

export interface IGetLog {
  page: number;
  size: number;
  fromDate: string;
  toDate: string;
  screenName?: string;
}

export interface WriteLog {
  logType: LogType;
  screenName: string;
  action: string;
  content: string;
}

interface LogOptional {
  companyId?: number;
  organizationId?: number;
  isNavigateToDiffPage?: boolean;
}

export interface IAddLog extends WriteLog {
  accessDate?: string;
  path?: string;
  companyId?: number;
  companyName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SaucerLogService {

  protected cacheCompany = [];
  protected cacheLogs = [];
  protected timer = null;

  constructor(private readonly http: ApiService) { }

  init() {
    this.writeBulkLogs();
  }

  serializeQuery(params: IGetLog) {
    return Object.keys(params)
      .map(key => {
        if (params[key] !== undefined && params[key] !== null) {
          return key + '=' + encodeURI(params[key]);
        }
      })
      .join('&');
  }

  getDataLog(options: IGetLog) {
    const url = API_ENDPOINT.saucerLog.getLog + '?' + this.serializeQuery(options);
    return this.http.get(url);
  }

  addDataLog(logData: WriteLog, optional: LogOptional = {}) {
    const logDto: IAddLog = { ...logData };
    logDto.accessDate = new Date().toISOString();
    logDto.path = location.pathname;
    logDto.companyId = 0;
    this.cacheLogs.push(logDto);
    let cLogs = this.getCacheLogStorage()
    if (cLogs?.length > 0) {
      cLogs.push(logDto);
    } else {
      cLogs = [logDto];
    }
    localStorage.setItem(LOCAL_STORAGE_KEY.cacheLogs, JSON.stringify(cLogs));
    if (logDto.action === LogAction.Logout) {
      this.writeBulkLogs();
      return;
    } else {
      if (optional.isNavigateToDiffPage) {
        this.writeBulkLogs();
        return;
      } else if (this.cacheLogs.length >= LogConfig.maxCachedItems) {
        this.writeBulkLogs();
        return;
      }
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.writeBulkLogs();
    }, LogConfig.maxTimeout);
  }

  protected async writeBulkLogs() {
    const cLogs = this.getCacheLogStorage()
    if (cLogs?.length > 0) {
      this.cacheLogs = [...cLogs];
      await this.callApiWriteBulkLogs([...cLogs]);
      let secondCacheLogs = this.getCacheLogStorage();
      secondCacheLogs = secondCacheLogs?.length > 0 ? secondCacheLogs : [];
      this.cacheLogs = secondCacheLogs.slice(cLogs.length);
      localStorage.setItem(LOCAL_STORAGE_KEY.cacheLogs, JSON.stringify(this.cacheLogs));
      clearTimeout(this.timer);
    }
  }

  protected callApiWriteBulkLogs(logs: IAddLog[]){
    return this.http.post(API_ENDPOINT.saucerLog.bulkLog, {logs}).toPromise();
  }

  getCacheLogStorage() {
    const cacheLogStorage = localStorage.getItem(LOCAL_STORAGE_KEY.cacheLogs);
    if (cacheLogStorage) {
      return JSON.parse(cacheLogStorage);
    }

    return [];
  }

  /**
   * Example: this.logService.actionLog('MST_JOB', LogAction.update, content);
   */
  actionLog(screenName: string, action: string, content: string, optional: LogOptional = {}) {
    if (!LogConfig.ActionLog.enable) {
      return;
    }
    this.addDataLog({ logType: LogType.ActionLog, screenName, action, content }, optional);
  }

  /**
   * Example: this.logService.logChangeScreen('MST_JOB', 'Menu > Master Job');
   */
  logChangeScreen(screenName: string, content: string, optional: LogOptional = {}): void {
    this.actionLog(screenName, LogAction.ChangeScreen, content, optional);
  }

  /**
   * Example: this.logService.logShowDialog('MST_JOB', 'Showing dialog edit job [01]');
   */
  logShowDialog(screenName: string, content: string, optional: LogOptional = {}) {
    this.actionLog(screenName, LogAction.ShowDialog, content, optional);
  }

  logLogin(screenName: string = ScreenName.login) {
    this.actionLog(screenName, LogAction.Login, 'ログイン', { companyId: 0, organizationId: 0 });
  }

  logLogout(screenName: string = ScreenName.businessManagement) {
    this.actionLog(screenName, LogAction.Logout, 'ログアウト');
  }
}
